import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axiosLib from 'axios';
import { BrickWallShield, KeyRound, SearchX } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';

import { NoData } from '@/components/legacy-ui/NoData';
import { NO_DATA_ICON_CLASS } from '@/components/legacy-ui/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { INTEGRATION_CATEGORIES } from '@/features/integrations/config/categories.config';
import { IntegrationCategory } from '@/features/integrations/types/category.types';
import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations, useIntegration } from '@/hooks/useIntegration';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { useGlobalState } from '@/state/global.state';
import type { GraphQueryResponse, NodeDetails, TreeNode } from '@/types';
import { Integration } from '@/types';

import { GraphResults } from './GraphResults';
import { GroupedResults } from './GroupedResults';
import { TableResults } from './TableResults';
import { transformTreeNodeToFlat } from './utils/extractQueryResults';

interface QueryResultsProps {
  selectedQuery: SavedQuery | null;
  onRiskKeysChange?: (keys: string[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

function extractRiskKeys(data: GraphQueryResponse | TreeNode[]): string[] {
  const dedupedKeys = new Set<string>();

  if (Array.isArray(data)) {
    data.forEach(tree => {
      const risks = tree?.node?.result?.risks;
      if (!Array.isArray(risks)) return;

      risks.forEach(risk => {
        if (risk?.key) {
          dedupedKeys.add(risk.key);
        }
      });
    });

    return [...dedupedKeys];
  }

  const risks = data?.risks;
  if (!Array.isArray(risks)) {
    return [];
  }

  risks.forEach(risk => {
    if (risk?.key) {
      dedupedKeys.add(risk.key);
    }
  });

  return [...dedupedKeys];
}

function buildQueryPayload(
  selectedQuery: SavedQuery,
  selectedEmails: string[],
  isRiskRoot: boolean,
  isGraphMode: boolean
) {
  let queryPayload = { ...selectedQuery.query };

  if (selectedEmails.length > 0)
    queryPayload = { ...queryPayload, usernames: selectedEmails };

  if (isRiskRoot && !isGraphMode) {
    const existingRels = queryPayload.node?.relationships || [];
    const hasInstanceOf = existingRels.some(
      r =>
        r.label === 'INSTANCE_OF' ||
        (Array.isArray(r.label) && r.label.includes('INSTANCE_OF'))
    );
    if (!hasInstanceOf) {
      queryPayload = {
        ...queryPayload,
        node: {
          ...queryPayload.node,
          relationships: [
            ...existingRels,
            {
              label: 'INSTANCE_OF' as const,
              target: { labels: ['Vulnerability'] } as NodeDetails,
              optional: true,
            },
          ],
        },
      };
    }
  }

  return queryPayload;
}

export const QueryResults = ({
  selectedQuery,
  onRiskKeysChange,
  onLoadingChange,
}: QueryResultsProps) => {
  const { usernames } = useQueryBuilder();
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { drawerState } = useGlobalState();

  const isWafCoverageQuery =
    selectedQuery?.id === 'internet-exposed-applications-without-waf';
  const isSsoCoverageQuery =
    selectedQuery?.id === 'internet-exposed-login-portals-without-sso';
  const {
    data: { connectedIntegrations },
    status: integrationStatus,
  } = useIntegration({ enabled: isWafCoverageQuery || isSsoCoverageQuery });
  const hasConnectedWafIntegration = connectedIntegrations.some(integration =>
    AllIntegrations[integration.member as Integration]?.tags?.includes('waf')
  );
  const hasConnectedIdpIntegration = connectedIntegrations.some(integration =>
    INTEGRATION_CATEGORIES[
      IntegrationCategory.IdentityAccessManagement
    ]?.includes(integration.member as Integration)
  );

  const isGraphMode = selectedQuery?.type === 'graph';
  const isTableTreeMode = selectedQuery?.type === 'table-tree';

  const rootLabels = selectedQuery?.query?.node?.labels || [];
  const isRiskRoot = rootLabels.includes('Risk');

  const selectedEmails = usernames.filter(u => u.isSelected).map(u => u.email);

  const isGroupedQuery = (selectedQuery?.query?.groupBy?.length ?? 0) > 0;
  const needsTree =
    !isGroupedQuery && (isGraphMode || isTableTreeMode || isRiskRoot);

  const queryPayload = selectedQuery
    ? buildQueryPayload(selectedQuery, selectedEmails, isRiskRoot, isGraphMode)
    : null;

  // Tree queries (graph view, table-tree, risk-root) use single-shot fetch
  // because the backend's ScanTree doesn't support offset pagination.
  const {
    data: treeData,
    isLoading: isTreeLoading,
    isFetching: isTreeFetching,
    isPlaceholderData: isTreePlaceholder,
    error: treeError,
  } = useQuery({
    queryKey: [
      'query-executor',
      selectedQuery?.id,
      selectedQuery?.type,
      selectedEmails,
      selectedQuery?.query,
    ],
    queryFn: async () => {
      if (!queryPayload) return null;

      const response = await axios.post('/my', queryPayload, {
        timeout: 30000,
        params: { tree: true },
      });

      const responseData = response.data;
      if (responseData?.error && typeof responseData.error === 'string') {
        throw new Error(responseData.error);
      }

      return responseData as TreeNode[];
    },
    enabled: !!selectedQuery && needsTree,
    staleTime: 30000,
    gcTime: 300000,
    retry: false,
    placeholderData: keepPreviousData,
  });

  // Flat queries use infinite query for offset-based pagination.
  const {
    data: flatPages,
    isLoading: isFlatLoading,
    isFetching: isFlatFetching,
    isPlaceholderData: isFlatPlaceholder,
    error: flatError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<GraphQueryResponse>({
    queryKey: [
      'query-executor-paged',
      selectedQuery?.id,
      selectedQuery?.type,
      selectedEmails,
      selectedQuery?.query,
    ],
    queryFn: async ({ pageParam }) => {
      if (!queryPayload) return {} as GraphQueryResponse;

      const payload =
        pageParam !== undefined
          ? { ...queryPayload, page: pageParam }
          : queryPayload;

      const response = await axios.post('/my', payload, {
        timeout: 30000,
      });

      const responseData = response.data;
      if (responseData?.error && typeof responseData.error === 'string') {
        throw new Error(responseData.error);
      }

      return responseData as GraphQueryResponse;
    },
    enabled: !!selectedQuery && !needsTree && !isGroupedQuery,
    staleTime: 30000,
    gcTime: 300000,
    retry: false,
    placeholderData: keepPreviousData,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: lastPage => {
      return lastPage?.offset ?? undefined;
    },
  });

  // Grouped queries use a dedicated infinite query since the response shape differs.
  const {
    data: groupedPages,
    isLoading: isGroupedLoading,
    isFetching: isGroupedFetching,
    isPlaceholderData: isGroupedPlaceholder,
    error: groupedError,
    fetchNextPage: fetchNextGroupedPage,
    hasNextPage: hasNextGroupedPage,
    isFetchingNextPage: isFetchingNextGroupedPage,
  } = useInfiniteQuery<GraphQueryResponse>({
    queryKey: [
      'query-executor-grouped',
      selectedQuery?.id,
      selectedQuery?.type,
      selectedEmails,
      selectedQuery?.query,
    ],
    queryFn: async ({ pageParam }) => {
      if (!queryPayload) return {} as GraphQueryResponse;

      const payload =
        pageParam !== undefined
          ? { ...queryPayload, page: pageParam }
          : queryPayload;

      const response = await axios.post('/my', payload, {
        timeout: 30000,
      });

      const responseData = response.data;
      if (responseData?.error && typeof responseData.error === 'string') {
        throw new Error(responseData.error);
      }

      return responseData as GraphQueryResponse;
    },
    enabled: !!selectedQuery && isGroupedQuery,
    staleTime: 30000,
    gcTime: 300000,
    retry: false,
    placeholderData: keepPreviousData,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: lastPage => lastPage?.offset ?? undefined,
  });

  const mergedGroupedData = useMemo(() => {
    if (!groupedPages?.pages?.length) return null;

    const allGroups: import('@/types').GroupedRow[] = [];
    let groupByFields: string[] = [];

    for (const page of groupedPages.pages) {
      if (page.groups) allGroups.push(...page.groups);
      if (page.groupBy && groupByFields.length === 0) {
        groupByFields = page.groupBy;
      }
    }

    return { groups: allGroups, groupBy: groupByFields };
  }, [groupedPages]);

  // Separate count query using groupBy — runs independently of the main data query.
  // Only for flat (non-tree, non-grouped) queries.
  const countPayload = useMemo(() => {
    if (!selectedQuery || needsTree || isGroupedQuery) return null;
    const base = selectedQuery.query;
    return {
      ...base,
      ...(selectedEmails.length > 0 ? { usernames: selectedEmails } : {}),
      groupBy: ['status'],
      limit: 10000,
      page: 0,
      orderBy: undefined,
      descending: undefined,
      orderByFields: undefined,
    };
  }, [selectedQuery, selectedEmails, needsTree, isGroupedQuery]);

  const { data: countData, isLoading: isCountLoading } = useQuery<{
    groups: { count: number }[];
  }>({
    queryKey: [
      'query-executor-count',
      selectedQuery?.id,
      selectedEmails,
      selectedQuery?.query,
    ],
    queryFn: async () => {
      if (!countPayload) return { groups: [] };
      const { data } = await axios.post('/my', countPayload, {
        timeout: 30000,
      });
      return data;
    },
    enabled: !!countPayload,
    staleTime: 30000,
    gcTime: 300000,
    retry: 1,
  });

  const totalCount = useMemo(() => {
    if (!countData?.groups) return undefined;
    return countData.groups.reduce((sum, group) => sum + (group.count || 0), 0);
  }, [countData]);

  // Merge flat pages into a single GraphQueryResponse for TableResults
  const mergedFlatData = useMemo((): GraphQueryResponse | null => {
    if (!flatPages?.pages?.length) return null;

    const merged: Record<string, unknown[]> = {};
    let totalFetchedCount = 0;

    for (const page of flatPages.pages) {
      for (const [key, value] of Object.entries(page)) {
        if (key === 'count' || key === 'offset') continue;
        if (Array.isArray(value)) {
          if (!merged[key]) merged[key] = [];
          merged[key].push(...value);
        }
      }
      totalFetchedCount += page.count ?? 0;
    }

    return { ...merged, count: totalFetchedCount } as GraphQueryResponse;
  }, [flatPages]);

  // Unify loading/error/data across tree, flat, and grouped modes
  const data = isGroupedQuery
    ? mergedGroupedData
    : needsTree
      ? treeData
      : mergedFlatData;
  const isLoading = isGroupedQuery
    ? isGroupedLoading
    : needsTree
      ? isTreeLoading
      : isFlatLoading;
  const isFetching = isGroupedQuery
    ? isGroupedFetching
    : needsTree
      ? isTreeFetching
      : isFlatFetching;
  const isPlaceholderData = isGroupedQuery
    ? isGroupedPlaceholder
    : needsTree
      ? isTreePlaceholder
      : isFlatPlaceholder;
  const error = isGroupedQuery
    ? groupedError
    : needsTree
      ? treeError
      : flatError;

  useEffect(() => {
    if (!onRiskKeysChange) {
      return;
    }

    if (!selectedQuery || !isRiskRoot || !data || isLoading || error) {
      onRiskKeysChange([]);
      return;
    }

    onRiskKeysChange(extractRiskKeys(data as GraphQueryResponse | TreeNode[]));
  }, [onRiskKeysChange, selectedQuery?.id, isRiskRoot, data, isLoading, error]);

  useEffect(() => {
    onLoadingChange?.(isLoading || isPlaceholderData);
  }, [onLoadingChange, isLoading, isPlaceholderData]);

  const invalidateQueryResults = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['query-executor'] });
    queryClient.invalidateQueries({ queryKey: ['query-executor-paged'] });
    queryClient.invalidateQueries({ queryKey: ['query-executor-grouped'] });
    queryClient.invalidateQueries({ queryKey: ['query-executor-count'] });
  }, [queryClient]);

  if (!selectedQuery) {
    return null;
  }

  if (isLoading || isPlaceholderData) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (error) {
    const errorMessage = axiosLib.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : error instanceof Error
        ? error.message
        : 'An error occurred while executing the query';

    return (
      <div
        className="flex h-full w-full items-center justify-center p-6"
        role="alert"
        aria-live="polite"
        data-testid="query-executor-error"
      >
        <div className="text-center">
          <p className="text-chariot-text-primary font-semibold">
            Query Execution Failed
          </p>
          <p className="text-chariot-text-secondary mt-2 text-sm">
            {errorMessage}
          </p>
        </div>
      </div>
    );
  }

  if (isWafCoverageQuery) {
    if (integrationStatus === 'pending') {
      return null;
    }
    if (integrationStatus === 'success' && !hasConnectedWafIntegration) {
      return (
        <NoData
          title="No WAF Integration Connected"
          description="Connect a Web Application Firewall integration to identify unprotected applications"
          icon={<BrickWallShield className={NO_DATA_ICON_CLASS} />}
          action={
            <Button
              onClick={() =>
                drawerState.handleOpenIntegrationDrawer(
                  undefined,
                  IntegrationCategory.Firewall
                )
              }
            >
              Add WAF Integration
            </Button>
          }
        />
      );
    }
  }

  const isEmpty = isGroupedQuery
    ? !mergedGroupedData || mergedGroupedData.groups.length === 0
    : !data ||
      (Array.isArray(data) && data.length === 0) ||
      (!Array.isArray(data) && (data as GraphQueryResponse).count === 0);

  if (isEmpty) {
    if (isSsoCoverageQuery && integrationStatus === 'pending') {
      return null;
    }

    if (
      isSsoCoverageQuery &&
      integrationStatus === 'success' &&
      !hasConnectedIdpIntegration
    ) {
      return (
        <NoData
          title="No Identity Provider Connected"
          description="Connect an Identity & Access Management integration to identify login portals without SSO protection"
          icon={<KeyRound className={NO_DATA_ICON_CLASS} />}
          action={
            <Button
              onClick={() =>
                drawerState.handleOpenIntegrationDrawer(
                  undefined,
                  IntegrationCategory.IdentityAccessManagement
                )
              }
            >
              Add Identity Provider
            </Button>
          }
        />
      );
    }

    return (
      <NoData
        title="No Results"
        description="This query returned no data"
        icon={<SearchX className={NO_DATA_ICON_CLASS} />}
      />
    );
  }

  if (isGroupedQuery && mergedGroupedData) {
    const aggregationAliases = (selectedQuery.query.aggregations ?? []).map(
      agg => `${agg.function}_${agg.field}`
    );
    return (
      <GroupedResults
        key={selectedQuery.id}
        groups={mergedGroupedData.groups}
        groupByFields={
          mergedGroupedData.groupBy.length > 0
            ? mergedGroupedData.groupBy
            : (selectedQuery.query.groupBy ?? [])
        }
        aggregationFields={aggregationAliases}
        isFetching={isGroupedFetching}
        hasNextPage={hasNextGroupedPage}
        fetchNextPage={fetchNextGroupedPage}
        isFetchingNextPage={isFetchingNextGroupedPage}
      />
    );
  }

  if (isGraphMode) {
    return <GraphResults key={selectedQuery?.id} data={data as TreeNode[]} />;
  }

  const needsTreeTransform = isTableTreeMode || (isRiskRoot && !isGraphMode);
  if (needsTreeTransform && Array.isArray(data)) {
    const flatData = transformTreeNodeToFlat(data as TreeNode[]);

    const firstTree = (data as TreeNode[])[0];
    const entityType = firstTree?.node?.result
      ? (Object.keys(firstTree.node.result).find(
          key => key !== 'count' && key !== 'offset'
        ) as keyof GraphQueryResponse | undefined)
      : undefined;

    const graphResponse: GraphQueryResponse = {
      [entityType || 'assets']: flatData,
      count: flatData.length,
    } as GraphQueryResponse;

    return (
      <TableResults
        key={selectedQuery?.id}
        data={graphResponse}
        isFetching={isFetching}
        onInvalidate={invalidateQueryResults}
      />
    );
  }

  const graphResponse = data as GraphQueryResponse;
  return (
    <TableResults
      key={selectedQuery?.id}
      data={graphResponse}
      totalCount={totalCount}
      isCountLoading={isCountLoading}
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      onInvalidate={invalidateQueryResults}
    />
  );
};
